// Company detail for a court-rental customer.
//
// One panel, two homes: the Company tab of a renter's profile, and the dialog
// behind their row on People → Customers for the organizations that never
// signed in (East River and Timber Creek have no account at all, so there is
// no profile to send them to). Before this the two were separate screens
// showing different things about the same organization — which is exactly why
// a renter's profile and their customer row disagreed.
//
// Self-contained on purpose: hand it a customer doc and it loads its own
// bookings, invoices and deposit, and owns the dialogs that edit them — the
// customer record itself included, through the same CustomerEditDialog the
// Customers tab uses.
//
// On a renter's profile it is split across tabs (`sections`), and given the
// renter's own uid: most renters book in the app themselves, so their court
// time is memberBookings under their account and their monthly invoice is keyed
// by memberId — none of which a customer record points at. Roberto Diaz has no
// customer record at all, and the panel showed him nothing.
import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, getDoc, doc, query, where,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { localToday } from '../utils/dates';
import { logError } from '../utils/errorLogger';
import CustomerEditDialog from './CustomerEditDialog';
import {
  Box, Typography, Paper, Grid, Chip, Button, IconButton, TextField, MenuItem,
  Checkbox, FormControlLabel, Alert, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Same palette as the rest of the light profile pages.
const STATUS_STYLE = {
  paid: { bg: '#E9F7EF', color: '#1E6B3C' },
  pending: { bg: '#FEF9E7', color: '#7B4800' },
  cancelled: { bg: '#EAECEE', color: '#566573' },
};

const InfoRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{label}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

const StatTile = ({ label, value, color, caption }) => (
  <Paper elevation={0} sx={{ flex: 1, minWidth: 130, p: 1.5, textAlign: 'center', borderRadius: 2, border: '1px solid #e0e4e8', backgroundColor: '#f8f9fb' }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block', fontSize: '0.7rem' }}>{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, color, fontSize: '1.1rem' }}>{value}</Typography>
    {caption ? <Typography variant="caption" sx={{ color: '#aaa' }}>{caption}</Typography> : null}
  </Paper>
);

const Section = ({ title, action, children }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', flexGrow: 1 }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </>
);

const ALL_SECTIONS = ['stats', 'company', 'reservations', 'deposit', 'invoices'];

export default function CompanyPanel({ customer = null, onCustomerChange, uid = null, rentalRateId = null, sections = ALL_SECTIONS }) {
  const show = (k) => sections.includes(k);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // The deposit lives on the linked user account, not the customer record —
  // null means this organization has no account to hold one.
  const [deposit, setDeposit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depositEdit, setDepositEdit] = useState(null);
  const [depositSaving, setDepositSaving] = useState(false);
  const [depositError, setDepositError] = useState('');
  const [invoiceEdit, setInvoiceEdit] = useState(null); // null = closed, {} = new
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  // An edit made here shows immediately even when the caller holds the customer
  // in state it won't refetch. Dropped as soon as a different customer arrives.
  const [edited, setEdited] = useState(null);
  // A renter's rate, when it comes from their membership rather than a
  // customer record.
  const [rate, setRate] = useState(null);

  const record = (edited && edited.id === customer?.id) ? edited : customer;
  const customerId = customer?.id;
  const customerUserId = customer?.userId || uid;

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setBookings([]); setInvoices([]); setDeposit(null); setRate(null);
      const found = [];
      if (customerId) {
        try {
          const snap = await getDocs(query(collection(db, 'rentalBookings'), where('customerId', '==', customerId)));
          found.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
          logError('CompanyPanel', 'customer bookings: ' + (e.message || String(e)), e.stack || '');
        }
      }
      // Court time the renter booked themselves. Priced on the booking as
      // overageAmount; it is paid at checkout or on the monthly invoice, never
      // on the booking, so it carries no paid status of its own.
      if (uid) {
        try {
          const snap = await getDocs(query(collection(db, 'memberBookings'), where('memberId', '==', uid)));
          found.push(...snap.docs.map(d => {
            const b = d.data();
            return { id: d.id, own: true, date: b.date, startTime: b.startTime, endTime: b.endTime,
              courts: b.courts || [], status: b.status, totalCost: Number(b.overageAmount) || 0 };
          }));
        } catch (e) {
          logError('CompanyPanel', 'renter bookings: ' + (e.message || String(e)), e.stack || '');
        }
      }
      if (!cancelled) setBookings(found.sort((a, b) =>
        `${b.date || ''}${b.startTime || ''}`.localeCompare(`${a.date || ''}${a.startTime || ''}`)));
      if (uid && rentalRateId && !(customer?.billingRateHourly != null && customer?.billingRateHourly !== '')) {
        try {
          const r = await getDoc(doc(db, 'rentalRates', rentalRateId));
          if (!cancelled && r.exists()) setRate(r.data());
        } catch (e) {
          logError('CompanyPanel', 'renter rate: ' + (e.message || String(e)), e.stack || '');
        }
      }
      if (customerUserId) {
        try {
          const uSnap = await getDoc(doc(db, 'users', customerUserId));
          const m = uSnap.exists() ? (uSnap.data().membership || {}) : {};
          if (!cancelled) setDeposit({
            uid: customerUserId,
            requiredCents: Math.max(0, Number(m.depositRequiredCents) || 0),
            paidCents: Math.max(0, Number(m.depositPaidCents) || 0),
          });
        } catch (e) {
          logError('CompanyPanel', 'customer deposit: ' + (e.message || String(e)), e.stack || '');
        }
      }
      try {
        // The month-end job keys a renter's invoice by memberId; ones raised
        // by hand for a company carry customerId. Both, once each.
        const queries = [];
        if (customerId) queries.push(getDocs(query(collection(db, 'customerInvoices'), where('customerId', '==', customerId))));
        if (uid) queries.push(getDocs(query(collection(db, 'customerInvoices'), where('memberId', '==', uid))));
        const byId = new Map();
        for (const snap of await Promise.all(queries)) snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
        if (!cancelled) setInvoices([...byId.values()]
          .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || '')));
      } catch (e) {
        // Collection may not exist yet — the rest of the panel still renders.
        logError('CompanyPanel', 'customer invoices: ' + (e.message || String(e)), e.stack || '');
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [customerId, customerUserId, uid, rentalRateId, customer?.billingRateHourly]);

  // Records a deposit received outside the app. The callable is the only thing
  // that writes the balance, so a cheque logged here and a card payment through
  // Stripe land in the same ledger with the same arithmetic.
  const saveDeposit = async () => {
    const dollars = parseFloat(depositEdit?.amount);
    if (!Number.isFinite(dollars) || dollars === 0) {
      setDepositError('Enter an amount. Use a negative number to refund.');
      return;
    }
    setDepositSaving(true); setDepositError('');
    try {
      const fn = httpsCallable(getFunctions(), 'recordRentalDeposit');
      const { data } = await fn({
        uid: deposit.uid,
        amountCents: Math.round(dollars * 100),
        method: depositEdit.method || 'manual',
        note: (depositEdit.note || '').trim(),
      });
      setDeposit(d => ({ ...d, paidCents: Number(data.depositPaidCents) || 0 }));
      setDepositEdit(null);
    } catch (e) {
      setDepositError(e.message || 'Could not record the deposit.');
    }
    setDepositSaving(false);
  };

  const saveInvoice = async () => {
    const f = invoiceEdit || {};
    const amount = Number(f.amount);
    if (!f.description?.trim()) { setInvoiceError('Description is required.'); return; }
    if (!Number.isFinite(amount)) { setInvoiceError('Amount must be a number.'); return; }
    if (f.paid && !f.paidDate) { setInvoiceError('Paid invoices need a paid date.'); return; }
    setInvoiceSaving(true); setInvoiceError('');
    try {
      const payload = {
        // No customer record: the invoice belongs to the renter's account, the
        // same key the month-end job uses.
        customerId: customerId || null,
        ...(customerId ? {} : { memberId: uid }),
        customerName: record?.organizationName || '',
        description: f.description.trim(),
        invoiceDate: f.invoiceDate || localToday(),
        amount,
        paid: !!f.paid,
        paidDate: f.paid ? (f.paidDate || localToday()) : '',
        updatedAt: serverTimestamp(),
      };
      if (f.id) {
        await updateDoc(doc(db, 'customerInvoices', f.id), payload);
        setInvoices(prev => prev.map(i => i.id === f.id ? { ...i, ...payload } : i)
          .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || '')));
      } else {
        const ref = await addDoc(collection(db, 'customerInvoices'), { ...payload, createdAt: serverTimestamp() });
        setInvoices(prev => [{ id: ref.id, ...payload }, ...prev]
          .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || '')));
      }
      setInvoiceEdit(null);
    } catch (e) {
      setInvoiceError(e.message || 'Save failed.');
    } finally { setInvoiceSaving(false); }
  };

  const deleteInvoice = async (inv) => {
    setInvoiceSaving(true);
    try {
      await deleteDoc(doc(db, 'customerInvoices', inv.id));
      setInvoices(prev => prev.filter(i => i.id !== inv.id));
    } catch (e) {
      setInvoiceError(e.message || 'Delete failed.');
    } finally { setInvoiceSaving(false); }
  };

  if (!record && !uid) return null;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#1E3A5F' }} />
      </Box>
    );
  }

  const mins = t => { const [h, m] = String(t || '0:0').split(':').map(Number); return h * 60 + (m || 0); };
  const active = bookings.filter(b => b.status !== 'cancelled');
  const courtHours = active.reduce(
    (s, b) => s + Math.max(0, (mins(b.endTime) - mins(b.startTime)) / 60) * ((b.courts || []).length || 1), 0);
  // A company's rental bookings carry their own paid status. A renter's own
  // bookings do not — what they owe is their unpaid monthly invoices.
  const unpaidBookings = active.filter(b => !b.own && b.status !== 'paid')
    .reduce((s, b) => s + (Number(b.totalCost) || 0), 0);
  const unpaidOwnInvoices = invoices.filter(i => !i.paid && i.memberId === uid && uid)
    .reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const unpaid = unpaidBookings + unpaidOwnInvoices;
  const rateHourly = record?.billingRateHourly != null && record?.billingRateHourly !== ''
    ? record.billingRateHourly : (rate ? Number(rate.hourlyRate) : null);
  const rateName = (record?.billingRateHourly != null && record?.billingRateHourly !== '')
    ? (record.billingRateName || '') : (rate?.rateName || '');
  const lastDate = active.reduce((m, b) => (b.date || '') > m ? b.date : m, '');

  const billed = invoices.reduce((t, i) => t + (Number(i.amount) || 0), 0);
  const invoicePaid = invoices.filter(i => i.paid).reduce((t, i) => t + (Number(i.amount) || 0), 0);
  const outstanding = Math.round((billed - invoicePaid) * 100) / 100;
  const owed = deposit ? Math.max(0, deposit.requiredCents - deposit.paidCents) : 0;

  return (
    <Box>
      {show('stats') && <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <StatTile label="Court Hours" color="#1A5276"
          value={courtHours % 1 === 0 ? courtHours : courtHours.toFixed(1)} />
        <StatTile label="Unpaid" color={unpaid > 0 ? '#922B21' : '#1E6B3C'} value={`$${unpaid.toFixed(2)}`} />
        <StatTile label="Last Reservation" color="#1E3A5F" value={lastDate || '—'} />
        <StatTile label="Billing Rate" color="#7B4800"
          value={rateHourly != null && rateHourly !== '' ? `$${rateHourly}/hr` : '—'}
          caption={rateName} />
      </Box>}

      {show('company') && record && <Section title="Company"
        action={
          <Button size="small" startIcon={<EditIcon />} sx={{ color: '#1E3A5F' }}
            onClick={() => setEditOpen(true)}>
            Edit
          </Button>
        }>
        <Grid container spacing={2}>
          <InfoRow label="Organization" value={record.organizationName} />
          <InfoRow label="Billing contact"
            value={`${record.firstName || ''} ${record.lastName || ''}`.trim()} />
          <InfoRow label="Email" value={record.email} />
          <InfoRow label="Phone" value={record.phone} />
        </Grid>
      </Section>}

      {show('reservations') && <Section title="Reservation History">
        {bookings.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#888' }}>No reservations yet.</Typography>
        ) : (
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e0e4e8', borderRadius: 2 }}>
            {bookings.map(b => {
              const style = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
              return (
                <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderBottom: '1px solid #eee', '&:last-child': { borderBottom: 'none' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.date} · {b.startTime}–{b.endTime}</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>{(b.courts || []).join(', ') || '—'}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                    ${(Number(b.totalCost) || 0).toFixed(2)}
                  </Typography>
                  <Chip size="small" label={b.status || 'pending'}
                    sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 600 }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Section>}

      {/* Deposit — only for a customer whose account actually has one */}
      {show('deposit') && deposit && (deposit.requiredCents > 0 || deposit.paidCents > 0) && (
        <Section title="Security deposit"
          action={
            <Button size="small" startIcon={<AddIcon />} sx={{ color: '#1E3A5F' }}
              onClick={() => { setDepositError(''); setDepositEdit({ amount: (owed / 100).toFixed(2), method: 'check' }); }}>
              Record payment
            </Button>
          }>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            {[['Required', deposit.requiredCents / 100, '#333'],
              ['Held', deposit.paidCents / 100, '#1E6B3C'],
              ['Outstanding', owed / 100, owed > 0 ? '#7B4800' : '#888']].map(([l, v, col]) => (
              <Box key={l} sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{l}</Typography>
                <Typography variant="body2" sx={{ color: col, fontWeight: 700 }}>${v.toFixed(2)}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
            {owed > 0
              ? 'They cannot book until this is settled. Held against the account, not spent on court time.'
              : 'Held against the account, not spent on court time. Enter a negative amount to refund it.'}
          </Typography>
        </Section>
      )}

      {show('invoices') && <Section title="Invoices"
        action={
          <Button size="small" startIcon={<AddIcon />} sx={{ color: '#1E3A5F' }}
            onClick={() => { setInvoiceError(''); setInvoiceEdit({ invoiceDate: localToday(), paid: false }); }}>
            Add invoice
          </Button>
        }>
        {invoices.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            {[['Billed', billed, '#333'],
              ['Paid', invoicePaid, '#1E6B3C'],
              ['Outstanding', outstanding, outstanding > 0 ? '#7B4800' : '#888']].map(([l, v, col]) => (
              <Box key={l} sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{l}</Typography>
                <Typography variant="body2" sx={{ color: col, fontWeight: 700 }}>${v.toFixed(2)}</Typography>
              </Box>
            ))}
          </Box>
        )}
        {invoiceError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setInvoiceError('')}>{invoiceError}</Alert>}
        {invoices.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#888' }}>No invoices yet.</Typography>
        ) : (
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e0e4e8', borderRadius: 2 }}>
            {invoices.map(inv => (
              <Box key={inv.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderBottom: '1px solid #eee', '&:last-child': { borderBottom: 'none' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.description}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {inv.invoiceDate}{inv.paid && inv.paidDate ? ` · paid ${inv.paidDate}` : ''}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                  ${(Number(inv.amount) || 0).toFixed(2)}
                </Typography>
                <Chip size="small" label={inv.paid ? 'Paid' : 'Unpaid'}
                  sx={{ backgroundColor: inv.paid ? STATUS_STYLE.paid.bg : STATUS_STYLE.pending.bg,
                    color: inv.paid ? STATUS_STYLE.paid.color : STATUS_STYLE.pending.color, fontWeight: 600 }} />
                <IconButton size="small" sx={{ color: '#888' }}
                  onClick={() => { setInvoiceError(''); setInvoiceEdit({ ...inv }); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" disabled={invoiceSaving} onClick={() => deleteInvoice(inv)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Section>}

      <CustomerEditDialog
        open={editOpen}
        customer={record}
        onClose={() => setEditOpen(false)}
        onSaved={saved => { setEdited(saved); onCustomerChange?.(saved); }} />

      {/* Record a deposit paid outside the app */}
      <Dialog open={Boolean(depositEdit)} onClose={() => setDepositEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record deposit payment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {depositError && <Alert severity="error" onClose={() => setDepositError('')}>{depositError}</Alert>}
          <TextField label="Amount" fullWidth size="small" type="number" autoFocus
            value={depositEdit?.amount ?? ''}
            onChange={e => setDepositEdit(f => ({ ...f, amount: e.target.value }))}
            inputProps={{ step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Negative to refund the deposit back to them." />
          <TextField label="Method" fullWidth size="small" select
            value={depositEdit?.method || 'check'}
            onChange={e => setDepositEdit(f => ({ ...f, method: e.target.value }))}>
            {['check', 'transfer', 'cash', 'other'].map(m => (
              <MenuItem key={m} value={m}>{m[0].toUpperCase() + m.slice(1)}</MenuItem>
            ))}
          </TextField>
          <TextField label="Note (optional)" fullWidth size="small"
            value={depositEdit?.note || ''}
            onChange={e => setDepositEdit(f => ({ ...f, note: e.target.value }))}
            helperText="Cheque number, who handed it over — whatever makes it traceable later." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveDeposit} disabled={depositSaving}>
            {depositSaving ? 'Saving…' : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer invoice add/edit */}
      <Dialog open={Boolean(invoiceEdit)} onClose={() => setInvoiceEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{invoiceEdit?.id ? 'Edit Invoice' : 'New Invoice'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {invoiceError && <Alert severity="error" onClose={() => setInvoiceError('')}>{invoiceError}</Alert>}
          <TextField label="Description" fullWidth size="small" autoFocus
            value={invoiceEdit?.description || ''}
            onChange={e => setInvoiceEdit(f => ({ ...f, description: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Invoice date" type="date" fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={invoiceEdit?.invoiceDate || ''}
              onChange={e => setInvoiceEdit(f => ({ ...f, invoiceDate: e.target.value }))} />
            <TextField label="Amount $" type="number" fullWidth size="small"
              value={invoiceEdit?.amount ?? ''}
              onChange={e => setInvoiceEdit(f => ({ ...f, amount: e.target.value }))} />
          </Box>
          <FormControlLabel
            control={
              <Checkbox checked={!!invoiceEdit?.paid}
                onChange={e => setInvoiceEdit(f => ({
                  ...f,
                  paid: e.target.checked,
                  // Marking paid defaults the date to today; unchecking clears it
                  // so an unpaid invoice never carries a stale paid date.
                  paidDate: e.target.checked ? (f.paidDate || localToday()) : '',
                }))} />
            }
            label="Paid" />
          {invoiceEdit?.paid && (
            <TextField label="Paid date" type="date" fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={invoiceEdit?.paidDate || ''}
              onChange={e => setInvoiceEdit(f => ({ ...f, paidDate: e.target.value }))} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceEdit(null)}>Cancel</Button>
          <Button variant="contained" disabled={invoiceSaving} onClick={saveInvoice}>
            {invoiceSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
